//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";
import { CSVAdminValidator } from "../../utils/csv/csv-validatorAdmin";

//* Import types from types
import {
  type Admin,
  type AdminImportResult,
  type AdminsDetails,
  type AdminResponse,
} from "./types/types";

// API methods for Admin operations
export const adminAPI = {
  // Fetch all Admins with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<AdminResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/admins?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          admins: data.admins || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching Admins:", error);
        throw new Error("Failed to fetch Admins");
      });
  },

  // Fetch a Admin by ID
  getById: async function (id: string, token: string): Promise<AdminsDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/admins/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching admin:", error);
        throw new Error("Failed to fetch Admin");
      });
  },

  // Create a new Admin
  create: async function (
    data: { name: string; email: string },
    token: string
  ): Promise<Admin> {
    try {
      const response = await api.request({
        url: `${API_URI}/admin/private/admins`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error creating admin:", error, data);
      throw error; 
    }
  },

  createBatch: async function (
    admins: CSVAdminValidator[],
    token: string
  ): Promise<AdminImportResult> {
    return api
      .request({
        url: `${API_URI}/admin/private/admins/batch`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { admins },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.error("Error processing CSVAdmin:", error);
        throw error;
      });
  },

  // Update an existing Admin
  update: async function (
    id: string,
    data: { name: string; email: string; reset_password?: boolean },
    token: string
  ): Promise<Admin> {
    return api
      .request({
        url: `${API_URI}/admin/private/admins/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating admin:", error);
        throw new Error("Failed to update Admin");
      });
  },

  // Delete an Admin by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/admins/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting admin:", error);
        throw new Error("Failed to delete Admin");
      });
  },

  // Restore an Admin by ID
  restoreByBatch: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/admins/restore/batch/${id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Admin:", error);
        throw new Error("Failed to restore Admin");
      });
  },

  // Restore an Admin by Email
  restoreByEmail: async function (email: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/admins/restore/email/`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { email },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Admin:", error);
        throw new Error("Failed to restore Admin");
      });
  }
};
